import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import './Inventory.css';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { downloadCsv } from '../../utils/exportCsv';
import { isAdmin as checkAdmin } from '../../utils/roleUtils';
import { formatAppDate } from '../../utils/dateUtils';

const InventoryList = forwardRef(({ onSelectItem, selectedItemId, onNew }, ref) => {
  const { t } = useTranslation('procurement');
  const { user } = useAuth();
  const isAdmin = checkAdmin(user);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('active');

  useImperativeHandle(ref, () => ({
    fetchMaterials
  }));

  // Debounce search input — wait 300ms after typing stops
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch when debounced search or type filter changes
  useEffect(() => {
    fetchMaterials();
  }, [typeFilter, debouncedSearch, activeFilter]);

  const fetchMaterials = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page_size: '100' });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (typeFilter !== 'all') params.set('item_type', typeFilter);
      // Inactive items are listed on request so they can be opened and reactivated
      if (activeFilter === 'inactive') params.set('is_active', 'false');
      if (activeFilter === 'all') params.set('include_inactive', 'true');
      const response = await apiFetch(`${API_CONFIG.ENDPOINTS.INVENTORY}?${params}`);
      if (response.ok) {
        const data = await response.json();
        setInventoryItems(data.items || []);
      } else {
        setError(t('materials.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching inventoryItems:', error);
      setError(t('materials.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  // Backend handles search, type and active filters
  const filteredMaterials = inventoryItems;

  const getTypeBadge = (materialType) => {
    const typeClass = materialType === 'RAW' ? 'type-raw' :
                     materialType === 'CONSUMABLE' ? 'type-consumable' :
                     materialType === 'PACKAGING' ? 'type-packaging' :
                     materialType === 'INK' ? 'type-ink' :
                     materialType === 'FINISHED_GOOD' ? 'type-finished_good' : 'type-raw';

    const displayText = materialType === 'RAW' ? t('materials.types.raw') :
                       materialType === 'CONSUMABLE' ? t('materials.types.consumable') :
                       materialType === 'PACKAGING' ? t('materials.types.packaging') :
                       materialType === 'INK' ? t('materials.types.ink') :
                       materialType === 'FINISHED_GOOD' ? t('materials.types.finishedGood') : materialType;

    return <span className={`type-badge ${typeClass}`}>{displayText}</span>;
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
  };

  const formatStock = (stock) => {
    if (!stock) return '0';
    return parseFloat(stock).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
  };

  const handleNewClick = () => {
    if (onNew) {
      onNew();
    }
  };

  if (loading && inventoryItems.length === 0) {
    return <div className="list-container"><div className="loading">{t('materials.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('materials.title')}</h2>
        <div className="header-actions">
          {isAdmin && (
            <button
              className="btn-secondary"
              onClick={() => downloadCsv('inventory')}
              title={t('common:actions.export', 'Export CSV')}
            >
              CSV
            </button>
          )}
          <button className="btn-primary" onClick={handleNewClick}>
            <i className="icon">+</i> {t('materials.newMaterial')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('materials.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('materials.filters.allTypes')}</option>
            <option value="RAW">{t('materials.filters.rawMaterial')}</option>
            <option value="CONSUMABLE">{t('materials.filters.consumable')}</option>
            <option value="PACKAGING">{t('materials.filters.packaging')}</option>
            <option value="INK">{t('materials.filters.ink')}</option>
            <option value="FINISHED_GOOD">{t('materials.filters.finishedGood')}</option>
          </select>
          <select
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
            className="filter-select"
          >
            <option value="active">{t('materials.filters.active')}</option>
            <option value="inactive">{t('materials.filters.inactive')}</option>
            <option value="all">{t('materials.filters.allStatuses')}</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th>{t('materials.tableHeaders.materialCode')}</th>
              <th>{t('materials.tableHeaders.materialName')}</th>
              <th>{t('materials.tableHeaders.type')}</th>
              <th>{t('materials.tableHeaders.costPerUnit')}</th>
              <th>{t('materials.tableHeaders.avgCost')}</th>
              <th>{t('materials.tableHeaders.stockStatus')}</th>
              <th>{t('materials.tableHeaders.unit')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredMaterials.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  {searchTerm ? t('materials.noData.searching') : t('materials.noData.empty')}
                </td>
              </tr>
            ) : (
              filteredMaterials.map(material => (
                <tr
                  key={material.item_id}
                  onClick={() => onSelectItem(material.item_id)}
                  className={selectedItemId === material.item_id ? 'selected' : ''}
                >
                  <td className="material-code">
                    <code>{material.item_code || t('materials.defaults.notApplicable')}</code>
                  </td>
                  <td className="material-info">
                    <div className="material-name">
                      <strong>{material.item_name || t('materials.defaults.unknown')}</strong>
                    </div>
                    {material.description && (
                      <div className="material-description">{material.description}</div>
                    )}
                  </td>
                  <td>
                    {getTypeBadge(material.item_type)}
                    {!material.is_active && (
                      <span className="status-badge status-inactive" style={{ marginLeft: '0.5rem' }}>
                        {t('materials.inactive')}
                      </span>
                    )}
                  </td>
                  <td className="stock-info">
                    {formatCurrency(material.cost_per_unit)}
                  </td>
                  <td className="stock-info">
                    {material.average_cost && parseFloat(material.average_cost) > 0 ? (
                      <div>
                        <strong>{formatCurrency(material.average_cost)}</strong>
                        {material.last_purchase_date && (
                          <div className="material-type" style={{ fontSize: '0.85em', color: '#888' }}>
                            {t('materials.stockInfo.last')} {formatAppDate(material.last_purchase_date)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#888' }}>-</span>
                    )}
                  </td>
                  <td className="stock-info">
                    <span className={`status-badge ${
                      material.stock_status === 'OUT_OF_STOCK' ? 'status-error' :
                      material.stock_status === 'CRITICAL' ? 'status-error' :
                      material.stock_status === 'REORDER' ? 'status-warning' :
                      material.stock_status === 'OVERSTOCKED' ? 'status-inactive' :
                      'status-success'
                    }`}>
                      {material.stock_status === 'OUT_OF_STOCK' ? t('materials.stockStatus.outOfStock') :
                       material.stock_status === 'CRITICAL' ? t('materials.stockStatus.critical') :
                       material.stock_status === 'REORDER' ? t('materials.stockStatus.reorder') :
                       material.stock_status === 'OVERSTOCKED' ? t('materials.stockStatus.overstocked') :
                       t('materials.stockStatus.normal')}
                    </span>
                    {material.current_stock !== undefined && (
                      <div className="material-type">
                        {t('materials.stockInfo.stock')} {formatStock(material.current_stock)}
                      </div>
                    )}
                  </td>
                  <td className="material-type">
                    {material.unit_of_measure || 'PCS'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="list-footer">
        <div className="record-count">
          {t('materials.recordCount', { filtered: filteredMaterials.length, total: inventoryItems.length })}
        </div>
      </div>
    </div>
  );
});

InventoryList.displayName = 'InventoryList';

export default InventoryList;